import React from "react";
import {Alert, Box, Checkbox, CircularProgress, ListItem, ListItemIcon, ListItemText} from "@mui/material";
import type {FilterAction, FilterOption} from "../reducers/includeExcludeFilterReducer";
import type {
    SelectAllState,
    TreeFilterItem,
} from "./treeViewFilterListSelectionUtils";
import {MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH} from "./treeViewFilterListSelectionUtils";
import {FilterValueTree} from "./FilterValueTree";

type TreeViewCheckboxContentProps = {
    isServerSide: boolean;
    serverError: string | null;
    searchTerm: string;
    selectedCategory: string;
    selectedCategoryLabel?: string;
    filterType: "include" | "exclude";
    dispatch: React.Dispatch<FilterAction>;
    selectAllState: SelectAllState | null;
    treeItems: TreeFilterItem[];
    isChecked: (option: FilterOption) => boolean;
    onToggle: (option: FilterOption) => void;
    searchActive: boolean;
    serverSearchLoading?: boolean;
};

export function TreeViewCheckboxContent({
    isServerSide,
    serverError,
    searchTerm,
    selectedCategory,
    selectedCategoryLabel,
    filterType,
    dispatch,
    selectAllState,
    treeItems,
    isChecked,
    onToggle,
    searchActive,
    serverSearchLoading,
}: TreeViewCheckboxContentProps) {
    return (
        <Box position="relative" flex={1} minHeight={0}>
            {(!isServerSide ||
                (!serverError &&
                    searchTerm.length >= MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH)) && (
                <Box
                    data-testid="tree-filter-list-scroll-container"
                    height="100%"
                    overflow="auto"
                    px={1}
                    py={1}
                >
                    {selectAllState && (
                        <ListItem
                            onClick={() =>
                                dispatch({
                                    type: "SELECT_ALL",
                                    payload: {filterType, categoryId: selectedCategory},
                                })
                            }
                            sx={{pl: 2}}
                        >
                            <ListItemIcon sx={{minWidth: "auto"}}>
                                <Checkbox
                                    edge="start"
                                    checked={selectAllState.checked}
                                    indeterminate={selectAllState.indeterminate}
                                    color="secondary"
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="Select All..."
                                slotProps={{
                                    primary: {style: {fontSize: "14px"}},
                                }}
                                sx={{my: 0, cursor: "pointer"}}
                            />
                        </ListItem>
                    )}

                    <FilterValueTree
                        treeItems={treeItems}
                        isChecked={isChecked}
                        onToggle={onToggle}
                        searchActive={searchActive}
                        expansionScope={filterType}
                    />
                </Box>
            )}

            {isServerSide &&
                serverSearchLoading &&
                searchTerm.length >= MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH && (
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{bgcolor: "rgba(255,255,255,0.6)", zIndex: 1}}
                    role="status"
                    aria-live="polite"
                    aria-label="Loading results"
                >
                    <CircularProgress size={32}/>
                </Box>
            )}

            {isServerSide && searchTerm.length < MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH && (
                <Alert severity="info" sx={{m: 2}}>
                    Please start by searching for a {" "}
                    {selectedCategoryLabel ?? selectedCategory} to use as a filter.
                </Alert>
            )}
        </Box>
    );
}

